import { useEffect, useRef, useState } from "react";
import { Globe } from "./globe/Globe";
import { Aggregator } from "./data/aggregator";
import { FlowBuffer } from "./data/buffer";
import { startDemoStream } from "./data/demo";
import {
  startReplayStream,
  emptyTodayTotals,
  emptyProductTotals,
  type TodayTotals,
  type DisplayStats,
} from "./data/replay";
import { ModeOrchestrator, type Mode } from "./modes/orchestrator";
import { Overlays } from "./overlays/Overlays";
import { latLngToPlane } from "./globe/projection";
import { resolvePrimary } from "./data/geocode";
import { colorNum } from "./theme/tokens";
import type {
  CorridorStat,
  SpotlightEvent,
  TransactionEvent,
} from "./data/types";
import type { MerchantStat } from "./overlays/TopMerchants";
import type { DataFreshness } from "./data/replay";

type LandingDom = {
  el: HTMLDivElement;
  worldX: number;
  worldY: number;
  startedAt: number;
  duration: number;
};

function formatLandingAmount(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `$${(n / 1000).toFixed(1)}K`;
  if (n >= 1000) return `$${(n / 1000).toFixed(2)}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

/** Format a local-currency amount compactly: "NGN 45,000" or "BTC 0.052". */
function formatLocal(ccy: string, amount: number | undefined): string {
  if (!amount || amount === 0) return ccy;
  const isCrypto = ["BTC", "ETH", "BNB", "XDT", "USDT", "USDC"].includes(ccy);
  if (isCrypto) {
    return amount < 1
      ? `${ccy} ${amount.toFixed(4)}`
      : `${ccy} ${amount.toFixed(2)}`;
  }
  if (amount >= 1_000_000) return `${ccy} ${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 10_000) return `${ccy} ${(amount / 1000).toFixed(1)}K`;
  return `${ccy} ${Math.round(amount).toLocaleString("en-US")}`;
}

const addPT = (
  a: { count: number; volumeUsd: number },
  b: { count: number; volumeUsd: number },
) => ({ count: a.count + b.count, volumeUsd: a.volumeUsd + b.volumeUsd });


// Force a full page reload on any HMR update — the Globe is instantiated
// imperatively, so partial HMR leaves stale state running.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    location.reload();
  });
}

export default function App() {
  const stageRef = useRef<HTMLDivElement>(null);
  const landingsRef = useRef<HTMLDivElement>(null);
  const burstsRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<Mode>("live");
  const [corridors, setCorridors] = useState<CorridorStat[]>([]);
  const [merchants, setMerchants] = useState<MerchantStat[]>([]);
  const [spotlight, setSpotlight] = useState<SpotlightEvent | null>(null);
  const [todayTotals, setTodayTotals] = useState<TodayTotals | null>(null);
  const [displayStats, setDisplayStats] = useState<DisplayStats | null>(null);
  const [freshness, setFreshness] = useState<DataFreshness | null>(null);

  useEffect(() => {
    if (!stageRef.current) return;
    const container = stageRef.current;
    const globe = new Globe(container);
    const aggregator = new Aggregator();
    const buffer = new FlowBuffer(8, 24, 200);
    const orchestrator = new ModeOrchestrator({
      aggregator,
      onMode: setMode,
      onSpotlight: setSpotlight,
      onRotationSlow: (slow) => globe.camera.setSlowMode(slow),
      onZoom: (countryA, countryB) => {
        const a = resolvePrimary(countryA);
        const b = resolvePrimary(countryB);
        if (!a || !b) return;
        const [x1, y1] = latLngToPlane(a.lat, a.lng);
        const [x2, y2] = latLngToPlane(b.lat, b.lng);
        globe.camera.zoomToCorridor(x1, y1, x2, y2);
      },
      onZoomReset: () => {
        globe.camera.resetZoom();
      },
    });

    let disposed = false;
    let stopDemo: (() => void) | undefined;
    const landings = new Map<string, LandingDom>();
    const creationBursts = new Map<string, LandingDom>();

    // Real-time totals = baseline (pre-tail events today) + live (events
    // emitted on the map during this session). The Set dedups so tail loops
    // don't double-count the same event.
    let baseline: TodayTotals = emptyTodayTotals();
    const live = {
      total: { count: 0, volumeUsd: 0 },
      byProduct: emptyProductTotals(),
    };
    const emittedIds = new Set<string>();
    const pushTotalsToUI = () => {
      const b = baseline.byProduct;
      const l = live.byProduct;
      const merged: TodayTotals = {
        sinceMs: baseline.sinceMs,
        total: {
          count: baseline.total.count + live.total.count,
          volumeUsd: baseline.total.volumeUsd + live.total.volumeUsd,
        },
        byProduct: {
          send_money: addPT(b.send_money, l.send_money),
          add_money: addPT(b.add_money, l.add_money),
          exchange_money: addPT(b.exchange_money, l.exchange_money),
          card: addPT(b.card, l.card),
          card_creation: addPT(b.card_creation, l.card_creation),
          other: addPT(b.other, l.other),
        },
      };
      setTodayTotals(merged);
    };

    const handleLanding = (ev: TransactionEvent) => {
      const root = landingsRef.current;
      if (!root) return;
      // Card spend: show merchant name even for small amounts (the merchant
      // name is the story). Other products: only show amounts above $25.
      // Show values on ALL actions — every event on the map gets a label.
      const hasName = ev.product === "card" && ev.merchant;
      const isSend = ev.product === "send_money";
      const [wx, wy] = latLngToPlane(ev.dest.lat, ev.dest.lng);
      const el = document.createElement("div");
      el.className =
        ev.amountUsd >= 500
          ? "landing landing-big"
          : hasName
            ? "landing landing-merchant"
            : "landing";
      const inner = document.createElement("div");
      inner.className = "landing-text";
      inner.textContent = hasName
        ? `${ev.merchant} · ${formatLandingAmount(ev.amountUsd)}`
        : isSend && ev.amountDstLocal
          ? formatLocal(ev.currencyDst, ev.amountDstLocal)
          : formatLandingAmount(ev.amountUsd);
      el.appendChild(inner);
      root.appendChild(el);
      landings.set(ev.id, {
        el,
        worldX: wx,
        worldY: wy,
        startedAt: performance.now(),
        duration: ev.amountUsd >= 500 ? 2800 : 1800,
      });
    };
    globe.onLanding = handleLanding;

    globe.init().then(async () => {
      if (disposed) return;

      globe.start();

      // Source selection: ?source=demo forces synthetic; default is replay
      // against yesterday's BigQuery data, with demo as a fallback if the
      // replay JSON isn't available (e.g. first-time dev without fetch).
      const params = new URLSearchParams(window.location.search);
      const forcedSource = params.get("source");

      const onEvent = (ev: TransactionEvent) => {
        aggregator.push(ev);

        // Real-time total tick — count every emitted event once.
        //
        // Gate by ORIGINAL transaction timestamp against today's midnight,
        // not by emission. Without this, replay-tail events from yesterday
        // inflate the live counter, and on refresh the new baseline (which
        // IS midnight-strict) is lower than (old baseline + inflated live),
        // so the displayed total visibly drops. With it, live only contains
        // today's events — same domain as baseline — and refreshes are
        // accurate to the latest BQ snapshot.
        //
        // Trade-off: at 00:00–00:0x UTC the display reads $0 until dbt
        // catches up. That is correct: yesterday's tail is not today's
        // volume, and showing it would be a lie.
        const txMs = ev.originalTs ?? ev.ts;
        if (txMs >= baseline.sinceMs && !emittedIds.has(ev.id)) {
          emittedIds.add(ev.id);
          live.total.count += 1;
          live.total.volumeUsd += ev.amountUsd;
          live.byProduct[ev.product].count += 1;
          live.byProduct[ev.product].volumeUsd += ev.amountUsd;
          pushTotalsToUI();
        }

        // Route by product.
        if (ev.product === "send_money" || ev.product === "card") {
          buffer.push(ev);
          orchestrator.observeEvent(ev);
          return;
        }
        if (ev.product === "add_money") {
          globe.pulseActivity(
            ev.origin.lat,
            ev.origin.lng,
            colorNum.productAdd,
          );
          // Label: "+" prefix + local currency (e.g., "+ NGN 45,000" or "+ BTC 0.05")
          fireProductLabel(ev, ev.origin, "add", `+ ${formatLocal(ev.currencySrc, ev.amountSrcLocal)}`);
          return;
        }
        if (ev.product === "exchange_money") {
          globe.pulseActivity(
            ev.origin.lat,
            ev.origin.lng,
            colorNum.productExchange,
          );
          // Label: currency pair (e.g., "NGN → USD")
          fireProductLabel(ev, ev.origin, "exchange", `${ev.currencySrc} → ${ev.currencyDst}`);
          return;
        }
        if (ev.product === "card_creation") {
          fireCreationBurst(ev);
          return;
        }
      };

      /** Fire a product-colored floating label at a given geo point. */
      const fireProductLabel = (
        ev: TransactionEvent,
        geo: { lat: number; lng: number },
        type: "add" | "exchange" | "send-origin",
        text: string,
      ) => {
        const root = landingsRef.current;
        if (!root) return;
        const [wx, wy] = latLngToPlane(geo.lat, geo.lng);
        const el = document.createElement("div");
        el.className = `landing landing-${type}`;
        const inner = document.createElement("div");
        inner.className = "landing-text";
        inner.textContent = text;
        el.appendChild(inner);
        root.appendChild(el);
        landings.set(`${ev.id}-${type}`, {
          el,
          worldX: wx,
          worldY: wy,
          startedAt: performance.now(),
          duration: 2200,
        });
      };

      const fireCreationBurst = (ev: TransactionEvent) => {
        const root = burstsRef.current;
        if (!root) return;
        const [wx, wy] = latLngToPlane(ev.origin.lat, ev.origin.lng);
        const el = document.createElement("div");
        el.className = "creation-burst";
        const inner = document.createElement("div");
        inner.className = "creation-burst-glyph";
        inner.textContent = "✦";
        el.appendChild(inner);
        root.appendChild(el);
        creationBursts.set(ev.id, {
          el,
          worldX: wx,
          worldY: wy,
          startedAt: performance.now(),
          duration: 1400,
        });
      };

      if (forcedSource !== "demo") {
        try {
          const handle = await startReplayStream({
            onEvent,
            onReady: (m) =>
              console.log(
                `[replay] tail window: ${m.count} events over ${m.spanMinutes.toFixed(0)} min`,
              ),
            onRefresh: (m) =>
              console.log(
                `[replay] +${m.newEvents} new events (total ${m.totalEvents})`,
              ),
            onTodayTotals: (t) => {
              // Whenever a new baseline arrives (load, manual refresh on
              // tab focus, or day rollover), reset live state so we don't
              // double-count events that are now part of baseline.
              baseline = t;
              live.total.count = 0;
              live.total.volumeUsd = 0;
              live.byProduct = emptyProductTotals();
              emittedIds.clear();
              pushTotalsToUI();
            },
            onCorridors: (c) => {
              setCorridors(c);
              orchestrator.setCorridors(c);
            },
            onMerchants: setMerchants,
            onCityActivity: (activity) => globe.updateCityActivity(activity),
            onStats: setDisplayStats,
            onFreshness: setFreshness,
          });

          // Force a refresh whenever the tab becomes visible again — browsers
          // throttle setInterval / requestAnimationFrame in background tabs,
          // so the display falls behind. On focus, pull the latest replay
          // and recompute baseline so the headline catches up immediately.
          let lastSeenDay = baseline.sinceMs;
          const onVisibility = () => {
            if (document.visibilityState !== "visible") return;
            handle.refreshNow().catch((e) =>
              console.warn("[replay] focus refresh failed", e),
            );
          };
          document.addEventListener("visibilitychange", onVisibility);

          // Day-rollover watchdog — at 00:00 UTC reset baseline + live and
          // wait for the next BQ refresh (or fire one immediately) to
          // populate the new day's number.
          const dayWatch = setInterval(() => {
            const todayMidnight = Date.UTC(
              new Date().getUTCFullYear(),
              new Date().getUTCMonth(),
              new Date().getUTCDate(),
            );
            if (todayMidnight !== lastSeenDay) {
              lastSeenDay = todayMidnight;
              handle.resetForNewDay();
              handle
                .refreshNow()
                .catch((e) =>
                  console.warn("[replay] day-rollover refresh failed", e),
                );
            }
          }, 30_000);

          stopDemo = () => {
            handle.stop();
            clearInterval(dayWatch);
            document.removeEventListener("visibilitychange", onVisibility);
          };
        } catch (err) {
          console.warn("[replay] falling back to demo:", err);
          stopDemo = startDemoStream({ tps: 3.5, onEvent });
        }
      } else {
        stopDemo = startDemoStream({ tps: 3.5, onEvent });
      }

      // Render/drain loop — independent of React.
      const drainLoop = () => {
        if (disposed) return;
        const now = performance.now();
        const drained = buffer.drain(now);
        for (const ev of drained) {
          globe.dispatch(ev, now);
          globe.camera.biasToLongitude(ev.dest.lng);
          // Origin label for send arcs: source currency at origin city.
          if (ev.product === "send_money" && ev.amountSrcLocal) {
            fireProductLabel(ev, ev.origin, "send-origin", formatLocal(ev.currencySrc, ev.amountSrcLocal));
          }
        }
        orchestrator.tick(now);

        // Landings — update positions, fade, remove.
        for (const [id, l] of landings) {
          const t = (now - l.startedAt) / l.duration;
          if (t >= 1) {
            l.el.remove();
            landings.delete(id);
            continue;
          }
          const scr = globe.projectToScreen(l.worldX, l.worldY);
          const rise = (1 - Math.pow(1 - t, 3)) * 22; // ease-out upward
          l.el.style.transform = `translate(${scr.x}px, ${scr.y - 14 - rise}px)`;
          const alpha =
            t < 0.12
              ? t / 0.12
              : t > 0.72
                ? Math.max(0, 1 - (t - 0.72) / 0.28)
                : 1;
          l.el.style.opacity = String(alpha);
        }

        // Creation bursts — "+" glyphs that drift up + fade. Longer rise
        // than landings so the growth moment reads as special.
        for (const [id, b] of creationBursts) {
          const t = (now - b.startedAt) / b.duration;
          if (t >= 1) {
            b.el.remove();
            creationBursts.delete(id);
            continue;
          }
          const scr = globe.projectToScreen(b.worldX, b.worldY);
          const rise = (1 - Math.pow(1 - t, 2.5)) * 38;
          b.el.style.transform = `translate(${scr.x}px, ${scr.y - 12 - rise}px)`;
          const alpha =
            t < 0.1
              ? t / 0.1
              : t > 0.55
                ? Math.max(0, 1 - (t - 0.55) / 0.45)
                : 1;
          b.el.style.opacity = String(alpha);
        }
        // Throttled state updates — React doesn't need 60fps.
        requestAnimationFrame(drainLoop);
      };
      requestAnimationFrame(drainLoop);
    });

    return () => {
      disposed = true;
      stopDemo?.();
      globe.dispose();
      for (const l of landings.values()) l.el.remove();
      landings.clear();
      for (const b of creationBursts.values()) b.el.remove();
      creationBursts.clear();
    };
  }, []);

  return (
    <>
      <div ref={stageRef} className="stage" />
      <div ref={landingsRef} className="landings" />
      <div ref={burstsRef} className="creation-bursts" />
      <Overlays
        mode={mode}
        todayTotals={todayTotals}
        corridors={corridors}
        merchants={merchants}
        spotlight={spotlight}
        displayStats={displayStats}
        freshness={freshness}
      />
    </>
  );
}
